import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { shallowEqual } from 'react-redux';

import { IconNameToComponent, IconType } from 'src/components/icons';
import { NotificationBadge } from 'src/components/NotificationBadge';
import * as Constants from 'src/constants';
import {
  API_SETTINGS_PAGE,
  APP_SETUP_PAGE,
  ASSISTANT_SETTINGS_PAGE,
  AUTH_SETTINGS_PAGE,
  BILLING_PAGE,
  CLIENT_NOTIFICATIONS_PAGE,
  CUSTOMIZATION_PAGE,
  DOMAIN_PAGE,
  EXTENSION_ITEM,
  NOTIFICATIONS_SETTINGS_PAGE,
  PAYMENTS_PAGE,
  PLAN_PAGE,
  PORTAL_ITEM,
  PROFILE_PAGE,
  SETTINGS_PAGE_ROOT_PATH,
  SIGNATURE_PAGE,
} from 'src/constants';
import { hasAppRouteAccess } from 'src/constants/appPermissions';
import {
  PortalConfigContext,
  RouteContext,
  useFeatureFlags,
} from 'src/context';
import { useAppLibraryLabel } from 'src/hooks/useAppLibraryLabel';
import { useNotificationBadgeData } from 'src/hooks/useNotificationBadgeData';
import { usePlanStatus } from 'src/hooks/usePlanStatusHook';
import { useSidebarNotificationCounts } from 'src/hooks/useSidebarNotificationCounts/useSidebarNotificationCounts';
import { useAppSelector } from 'src/hooks/useStore';
import { useUserAccess } from 'src/hooks/useUserAccess';
import { useUserCanInvite } from 'src/hooks/useUserCanInvite';
import { ContractPageTabs } from 'src/legacy/components/Contracts';
import { ExtensionTooltipTitle } from 'src/legacy/components/Tooltip';
import { useRouteAccess } from 'src/routes/routeAccess';
import { useGetPortalConfigQuery } from 'src/services/api';
import { useGetInstallsQuery } from 'src/services/api/applicationsApi';
import { useGetProductStoresQuery } from 'src/services/api/productStoreApi';
import { RootState } from 'src/store';
import { getModuleIcon, getModuleName } from 'src/utils/ModuleSettingsUtils';

import { nestItemsInFolders } from './nestItemsInFolders';
import { InboxNotificationBadge } from './NotificationBadges';
import { NavItem, NavSection } from './SidebarNav';
import { UpgradeBadge } from './UpgradeBadge';

interface CoreModulesNavItems {
  /**
   * Core modules without folders (no nested nav items except for billing).
   * This provides compatibility with existing useSidebarItems usage
   */
  navItems: NavItem[];
  /** Core modules with nested folder nav items */
  navItemsWithFolders?: NavItem[];
}

export const appNavItemIdPrefix = 'sidebar-app-';
export const moduleNavItemIdPrefix = 'sidebar-';

// TODO: delete this when removing the EnableAppSetup feature flag
export enum CoreModuleSortOrder {
  MESSAGES = 0,
  FILES = 1,
  CONTRACTS = 2,
  FORMS = 3,
  PAYMENTS = 4,
  KNOWLEDGE_BASE = 5,
  EXTENSIONS = 6,
}

enum ExtensionItemViewLocation {
  IN_PORTAL = 'in-portal',
  NEW_TAB = 'new-tab',
}

export type SidebarType = 'main' | 'settings';

export interface UseSidebarItemsResult {
  sections: Array<NavSection>;
  isLoadingInstalls: boolean;
}

export const useSidebarItems = (
  sidebarType?: SidebarType,
  opts: { shouldPreviewAsClient?: boolean } = {},
): UseSidebarItemsResult => {
  const { shouldPreviewAsClient = false } = opts;
  const { t } = useTranslation();
  const { routeAccess } = useRouteAccess({ shouldPreviewAsClient });
  const { pathname, query } = useContext(RouteContext);
  const planStatus = usePlanStatus();
  const { isInviteConfigDisabled } = useUserCanInvite();
  const companiesDisabled =
    !!useContext(PortalConfigContext).features.disableCompanies;
  const {
    EnableManyCompanies,
    EnableAppSetup,
    EnableBillingConsolidation,
    EnableIUAccessControl,
  } = useFeatureFlags();
  // check if logged in user has permission to adjust internal role
  // and invite config is not disabled
  const hasModifyInternalRolePermission =
    !isInviteConfigDisabled &&
    useUserAccess(
      Constants.PERM_MOD_SETTINGS_MEMBERS,
      Constants.USER_OPERATIONS.AdjustInternalRole,
    );
  const activeCompanyId = useAppSelector(
    (state: RootState) => state.activeCompany.activeCompanyId,
  );
  const {
    data: { moduleSettings, extensionsSettings } = {},
    isLoading: isLoadingInstalls,
  } = useGetInstallsQuery(
    EnableAppSetup
      ? shouldPreviewAsClient
        ? 'withFoldersPreview'
        : 'withFolders'
      : undefined,
  );
  const { id: queryIdParam } = query;
  const { isClient, extensionItems, lastVisitedCrmTabRoute } = useAppSelector(
    (state: RootState) => ({
      extensionItems: state.dashboard.items,
      isClient: state.user.isClient || shouldPreviewAsClient === true,
      lastVisitedCrmTabRoute: state.clients.lastVisitedTab,
    }),
    shallowEqual,
  );
  const hasAutomationsAccess = useUserAccess(
    Constants.PERM_MOD_AUTOMATIONS,
    '',
  );
  const { data: portalConfigurations } = useGetPortalConfigQuery();
  const shouldFilterNonClientItems =
    isClient && portalConfigurations?.structFields?.disableCompanies;

  const setupExtensionConfigSet = extensionItems
    .filter((extensionItem) => {
      const isActiveCompanyMatch = extensionItem.fields.companyID
        ? extensionItem.fields.companyID === activeCompanyId
        : true;
      const isCompanyMatch = !EnableManyCompanies ? true : isActiveCompanyMatch;
      return isCompanyMatch && extensionItem.fields.status === 'connected';
    })
    .reduce(
      (extensionConfigSet: Record<string, boolean>, extensionItem) => ({
        ...extensionConfigSet,
        [extensionItem.fields.extensionConfigId || 'default']: true,
      }),
      {},
    );
  const { data: stores = [] } = useGetProductStoresQuery();
  const store = stores.at(0);

  const meSectionNavItems: Array<NavItem> = [
    {
      htmlId: 'account-setting-page',
      label: PROFILE_PAGE.label,
      path: PROFILE_PAGE.path,
      icon: PROFILE_PAGE.icon,
      canAccess: true,
      iconName: 'user',
    },

    {
      htmlId: 'notification-setting-page',
      label: NOTIFICATIONS_SETTINGS_PAGE.label,
      path: NOTIFICATIONS_SETTINGS_PAGE.path,
      icon: NOTIFICATIONS_SETTINGS_PAGE.icon,
      iconName: 'bell',
      canAccess: routeAccess.allowNotificationSettings,
    },
  ];

  const { messages, files, invoices, forms, contracts, apps } =
    useSidebarNotificationCounts();
  const { backgroundColor, textColor } = useNotificationBadgeData(true);

  /**
   * This function calculate for each extension sidebar item
   * the external link url and the extension view location (in portal or new tab).
   *
   * @param extensionConfig
   * @param extId
   * @returns
   */
  const getExtensionLinkAccessibilityData = (
    extensionConfig: Constants.ExtensionConfig,
    extId: string,
  ) => {
    const defaultExtensionPath = `${Constants.CLIENT_APPS_PAGE.path}?id=${extId}`;
    let extensionItemAccessibilityData = {
      extensionLink: defaultExtensionPath,
      viewExtensionLocation: ExtensionItemViewLocation.IN_PORTAL,
    };
    if (extensionConfig.type === 'local') {
      // find all local connected extension channels that match this extensionID
      let connectedLocalExtensionItems = extensionItems.filter(
        (extensionItem) =>
          extensionItem.fields.extensionConfigId === extId &&
          extensionItem.fields.status === 'connected' &&
          extensionItem.fields.companyID === activeCompanyId,
      );
      if (EnableManyCompanies) {
        connectedLocalExtensionItems = connectedLocalExtensionItems.filter(
          (extensionItem) => extensionItem.fields.companyID === activeCompanyId,
        );
      }

      const connectedLocalExtensionItem =
        connectedLocalExtensionItems.length > 0
          ? connectedLocalExtensionItems.at(0)
          : null;

      if (connectedLocalExtensionItem) {
        const hasMoreThanOneConnectedEmbed =
          connectedLocalExtensionItems.length > 1;
        // default view local extensions in portal
        let viewExtensionLocation = ExtensionItemViewLocation.IN_PORTAL;
        // when client is viewing extension with embedLink and only has one channel then view in new tab
        if (
          isClient &&
          !hasMoreThanOneConnectedEmbed &&
          connectedLocalExtensionItem.fields.embedLink
        ) {
          viewExtensionLocation = ExtensionItemViewLocation.NEW_TAB;
        }
        extensionItemAccessibilityData = {
          ...extensionItemAccessibilityData,
          extensionLink:
            isClient &&
            connectedLocalExtensionItem.fields.embedLink &&
            !hasMoreThanOneConnectedEmbed
              ? connectedLocalExtensionItem.fields.embedLink
              : defaultExtensionPath,
          viewExtensionLocation,
        };
      }
    } else {
      // global extension
      extensionItemAccessibilityData = {
        ...extensionItemAccessibilityData,
        extensionLink:
          isClient && extensionConfig.embedLink
            ? extensionConfig.embedLink
            : defaultExtensionPath,
        viewExtensionLocation:
          isClient && extensionConfig.embedLink
            ? ExtensionItemViewLocation.NEW_TAB
            : ExtensionItemViewLocation.IN_PORTAL,
      };
    }

    return extensionItemAccessibilityData;
  };

  /**
   * This function calculates the CRM path based on the user's last visited crm tab.
   * @returns CRM path based on the user's last visited crm tab
   */
  const getCrmPath = () => {
    if (!companiesDisabled) {
      // check if there is a last visited crm path in local storage
      return lastVisitedCrmTabRoute || Constants.COMPANIES_PAGE.path;
    }

    return Constants.CRM_CLIENTS_PAGE.path;
  };

  const extensionNavItems: NavItem[] = routeAccess.allowExtensionsPage
    ? Object.entries(extensionsSettings || {})
        .filter(([extensionID]) =>
          (moduleSettings || []).some((module) => extensionID === module.id),
        )
        .filter(
          ([extensionID, extensionConfig]) =>
            isClient
              ? ['global'].includes(extensionConfig.type) ||
                setupExtensionConfigSet[extensionID]
              : true, // show all extensions for internal users
        )
        .filter(([, extensionConfig]) => {
          // Filter extensions by routeAccess (when flag enabled)
          if (!EnableIUAccessControl) return true; // feature flag not enabled

          const { appId } = extensionConfig;
          if (!appId) return true; // no app ID means no routeAccess requirement

          return hasAppRouteAccess(appId, routeAccess);
        })
        .sort(
          ([, extensionConfigA], [, extensionConfigB]) =>
            // first order by extension type so global is before local
            // then by `sort` which is number indicating position in the sidebar
            extensionConfigA.type.localeCompare(extensionConfigB.type) ||
            extensionConfigA.sort - extensionConfigB.sort,
        )
        .map(([extensionId, extensionConfig]) => {
          const { extensionLink: extensionItemPath, viewExtensionLocation } =
            getExtensionLinkAccessibilityData(extensionConfig, extensionId);

          const notificationCount = extensionConfig.appId
            ? (apps[extensionConfig.appId] ?? 0)
            : 0;

          return {
            htmlId: `${appNavItemIdPrefix}${extensionId}`,
            label: extensionConfig.name,
            isNonCoreApp: true,
            isExternalLink:
              viewExtensionLocation === ExtensionItemViewLocation.NEW_TAB,
            path: extensionItemPath,
            icon: IconNameToComponent[extensionConfig.icon],
            iconName: extensionConfig.icon,
            canAccess: true,
            children: !extensionConfig.appId ? null : (
              <NotificationBadge
                showZero={false}
                isSidebar
                content={notificationCount}
                backgroundColor={backgroundColor}
                textColor={textColor}
              />
            ),
            notificationCount,
            selected: queryIdParam === extensionId,
            disabled: planStatus.isBasicPlan,
            tooltipTitle: planStatus.isBasicPlan ? (
              <ExtensionTooltipTitle />
            ) : null,
            priority: (moduleSettings || []).findIndex(
              (x) => x.id === extensionId,
            ),
            queryParams: extensionConfig.queryParams,
          };
        })
        .sort((a, b) => a.priority - b.priority)
    : [];

  const crmNavItem = {
    htmlId: 'sidebar-crm',
    label: Constants.CRM_CLIENTS_PAGE.label,
    path: getCrmPath(),
    icon: Constants.CRM_CLIENTS_PAGE.icon,
    iconName: 'user-group',
    canAccess: routeAccess.allowCRMPages,
    selected:
      pathname.includes('/companies') ||
      pathname.includes('/clients') ||
      pathname.includes('/import'),
  };

  const inboxNavItem = {
    htmlId: 'sidebar-inbox',
    // when other entities invoices notifications are shown in inbox
    // then the inbox page sidebar item label should show 'Notifications'
    // rather than 'Files notifications'
    label: Constants.INBOX_PAGE.label,
    path: Constants.INBOX_PAGE.path,
    icon: Constants.INBOX_PAGE.icon,
    iconName: 'bell',
    children: <InboxNotificationBadge />,
    canAccess: routeAccess.allowInboxPage,
    selected: pathname.includes(Constants.INBOX_PAGE.path),
  };

  const extensionNavItem = {
    htmlId: 'extensions-item',
    label: EXTENSION_ITEM.label,
    path: extensionNavItems.at(0)?.path || '',
    icon: EXTENSION_ITEM.icon,
    canAccess: !isClient && extensionNavItems.length > 0, // Hide extensions main item if user is client or there are no extension items
    subItems: extensionNavItems,
    shouldCollapse: !!queryIdParam,
    priority: CoreModuleSortOrder.EXTENSIONS,
    tooltipTitle: planStatus.isBasicPlan ? <ExtensionTooltipTitle /> : null,
  };

  const homeNavItem = {
    htmlId: 'sidebar-home',
    label: Constants.HOME_PAGE.label,
    path: Constants.HOME_PAGE.path,
    icon: Constants.HOME_PAGE.icon,
    iconName: 'house',
    canAccess: routeAccess.allowHomePage,
    // home sidebar item is selected when user routes to home page
    // or trying to access a specific portal e.g. `/portal/${portalId}`
    selected:
      pathname === Constants.HOME_PAGE.path ||
      pathname.startsWith(Constants.PORTAL_PATH),
  };

  const automationNavItem = {
    htmlId: 'sidebar-automations',
    label: Constants.AUTOMATIONS_PAGE.label,
    path: `${Constants.AUTOMATIONS_PAGE.path}`,
    icon: Constants.AUTOMATIONS_PAGE.icon,
    canAccess: routeAccess.allowAutomationsPage,
    iconName: 'Automation',
    selected: pathname.includes(Constants.AUTOMATIONS_PAGE.path),
  };

  const defaultPortalSectionNavItems: NavItem[] = React.useMemo(
    () => [
      {
        htmlId: 'sidebar-messages',
        label: Constants.MESSAGES_PAGE.label,
        path: Constants.MESSAGES_PAGE.path,
        icon: Constants.MESSAGES_PAGE.icon,
        canAccess: routeAccess.allowMessagesPage,
        children:
          messages < 1 ? null : (
            <NotificationBadge
              htmlId="messages-notification-badge"
              isSidebar
              showZero={false}
              content={messages}
              backgroundColor={backgroundColor}
              textColor={textColor}
            />
          ),
        notificationCount: messages,
        priority: CoreModuleSortOrder.MESSAGES,
      },
      {
        htmlId: 'sidebar-files',
        label: Constants.FILES_PAGE.label,
        path: Constants.FILES_PAGE.path,
        icon: Constants.FILES_PAGE.icon,
        canAccess: routeAccess.allowFilesPage,
        children:
          // When files notification center flag is turned on
          // don't show files sidebar items notification count badge( internal user only )
          !isClient ? null : (
            <NotificationBadge
              showZero={false}
              isSidebar
              content={files}
              backgroundColor={backgroundColor}
              textColor={textColor}
            />
          ),
        notificationCount: files,
        priority: CoreModuleSortOrder.FILES,
        selected: pathname.includes(SIGNATURE_PAGE.path),
      },
      {
        htmlId: 'sidebar-contracts',
        label: Constants.CONTRACTS_PAGE.label,
        path: `${Constants.CONTRACTS_PAGE.path}?view=${ContractPageTabs.Templates}`,
        icon: Constants.CONTRACTS_PAGE.icon,
        canAccess: routeAccess.allowContractsPage,
        priority: CoreModuleSortOrder.CONTRACTS,
        selected: pathname.startsWith(Constants.CONTRACTS_PAGE.path),
        children: (
          <NotificationBadge
            showZero={false}
            isSidebar
            content={contracts}
            backgroundColor={backgroundColor}
            textColor={textColor}
          />
        ),
        notificationCount: contracts,
      },
      {
        htmlId: 'sidebar-forms',
        label: Constants.FORMS_PAGE.label,
        path: Constants.FORMS_PAGE.path,
        icon: Constants.FORMS_PAGE.icon,
        canAccess: isClient
          ? routeAccess.allowFormsClient
          : routeAccess.allowFormsInternal,
        children: !isClient ? null : (
          <NotificationBadge
            showZero={false}
            isSidebar
            content={forms}
            backgroundColor={backgroundColor}
            textColor={textColor}
          />
        ),
        notificationCount: forms,
        priority: CoreModuleSortOrder.FORMS,
        selected: pathname.startsWith(Constants.FORMS_PAGE.path),
      },
      EnableBillingConsolidation && !isClient
        ? {
            htmlId: 'sidebar-payments',
            label: Constants.PAYMENT_PAGE.label,
            path: Constants.PAYMENT_PAGE.path,
            icon: Constants.PAYMENT_PAGE.icon,
            priority: CoreModuleSortOrder.PAYMENTS,
            canAccess: routeAccess.allowPaymentsPage,
          }
        : {
            htmlId: 'sidebar-payments',
            label: 'Billing',
            path: !isClient
              ? Constants.PRODUCTS_PAGE.path
              : Constants.INVOICES_PAGE.path,
            icon: Constants.INVOICES_PAGE.icon,
            priority: CoreModuleSortOrder.PAYMENTS,
            canAccess:
              routeAccess.allowPaymentsPage &&
              routeAccess.allowSubscriptionPage,
            shouldCollapse:
              pathname === Constants.INVOICES_PAGE.path ||
              pathname === Constants.SUBSCRIPTION_PAGE.path,
            subItems: [
              ...(!isClient
                ? [
                    {
                      htmlId: 'sidebar-products',
                      label: Constants.PRODUCTS_PAGE.label,
                      path: EnableBillingConsolidation
                        ? Constants.PAYMENTS_SERVICES_PAGE.path
                        : Constants.PRODUCTS_PAGE.path,
                      canAccess:
                        routeAccess.allowPaymentsPage &&
                        routeAccess.allowSubscriptionPage,
                    },
                  ]
                : []),
              ...[
                {
                  htmlId: 'sidebar-store',
                  label: Constants.STORE_PAGE.label,
                  path: EnableBillingConsolidation
                    ? Constants.PAYMENTS_STORE_PAGE.path
                    : Constants.STORE_PAGE.path,
                  iconName: 'Store',
                  canAccess:
                    routeAccess.allowProductStore &&
                    routeAccess.allowSubscriptionPage,
                },
              ],
              {
                htmlId: 'sidebar-invoices',
                label: Constants.INVOICES_PAGE.label,
                path: EnableBillingConsolidation
                  ? Constants.PAYMENTS_INVOICES_PAGE.path
                  : Constants.INVOICES_PAGE.path,
                canAccess:
                  routeAccess.allowInvoices &&
                  routeAccess.allowSubscriptionPage,
              },
              {
                htmlId: 'sidebar-subscriptions',
                label: Constants.SUBSCRIPTION_PAGE.label,
                path: EnableBillingConsolidation
                  ? Constants.PAYMENTS_SUBSCRIPTIONS_PAGE.path
                  : Constants.SUBSCRIPTION_PAGE.path,
                canAccess:
                  routeAccess.allowPaymentsPage &&
                  routeAccess.allowSubscriptionPage,
              },
              {
                htmlId: 'sidebar-payment-links',
                label: Constants.PAYMENT_LINKS_PAGE.label,
                path: EnableBillingConsolidation
                  ? Constants.PAYMENTS_PAYMENT_LINKS_PAGE.path
                  : Constants.PAYMENT_LINKS_PAGE.path,
                canAccess:
                  routeAccess.allowPaymentsPage &&
                  routeAccess.allowSubscriptionPage,
              },
            ],
          },
      {
        htmlId: 'sidebar-client-store',
        label: store?.displayName || Constants.STORE_PAGE.label,
        path: Constants.STORE_PAGE.path,
        icon: Constants.STORE_PAGE.icon,
        iconName: 'Store',
        canAccess:
          routeAccess.allowPaymentsPage &&
          routeAccess.allowClientProductStore &&
          Boolean(store),
      },
      {
        htmlId: 'sidebar-payments', // payments for clients
        label: 'Billing',
        path: Constants.CLIENT_BILLING_PAGE.path,
        icon: Constants.CLIENT_BILLING_PAGE.icon,
        canAccess:
          routeAccess.allowClientPaymentsPage &&
          !routeAccess.allowSubscriptionPage,
        children: (
          <NotificationBadge
            showZero={false}
            isSidebar
            content={invoices}
            backgroundColor={backgroundColor}
            textColor={textColor}
          />
        ),
        notificationCount: invoices,
        priority: CoreModuleSortOrder.PAYMENTS,
      },
      {
        htmlId: 'sidebar-knowledgeBase',
        label: Constants.HELPDESK_PAGE.label,
        path: Constants.HELPDESK_PAGE.path,
        icon: Constants.HELPDESK_PAGE.icon,
        canAccess: routeAccess.allowKnowledgeBasePage,
        priority: CoreModuleSortOrder.KNOWLEDGE_BASE,
        selected: pathname.includes(Constants.HELPDESK_PAGE.path),
      },
    ],
    [routeAccess],
  );

  const appLibraryLabel = useAppLibraryLabel();

  const getPortalNavItems = () => [
    {
      htmlId: 'sidebar-modules-settings',
      label: appLibraryLabel,
      path: APP_SETUP_PAGE.path,
      canAccess: routeAccess.allowSettingsModuleManagement,
      icon: APP_SETUP_PAGE.icon,
      iconName: 'sliders',
      selected: pathname.includes(APP_SETUP_PAGE.path),
    },
    {
      htmlId: 'sidebar-portal-customization',
      label: CUSTOMIZATION_PAGE.label,
      path: '/customization',
      icon: PORTAL_ITEM.icon,
      iconName: 'Customization',
      canAccess: routeAccess.allowSettingsCustomizationPage,
    },
  ];

  /**
   * This function determines portal section nav items
   * from modules settings.
   * @returns
   */
  const getCoreModulesFromModuleSettings = (): CoreModulesNavItems => {
    // when module settings is not defined return the default portal sections
    // navigation items.
    const modules = moduleSettings || [];

    // When a new extension is created, it is added to the extensionsSettings and thus to be taken account of in sidebar

    let allNavItems = defaultPortalSectionNavItems;

    if (isClient || EnableAppSetup) {
      allNavItems = allNavItems.concat(extensionNavItems); // Only concat for client. For admin, display as subItems
    }

    const clientNavItems = modules
      .map((module): NavItem | null => {
        if (module.disabled) {
          return null;
        }

        if (module.type === 'folder' && EnableAppSetup)
          return {
            htmlId: `sidebar-folder-${module.id}`,
            label: module.label,
            icon: IconNameToComponent[module.icon as IconType],
            iconName: module.icon,
            path: '',
            id: module.id,
            isFolder: true,
            subItems: [],
            isHidden: !isClient && module.iuSidebarHidden,
          };

        const moduleNavItem = allNavItems.find((item) => {
          const itemId = item.htmlId.includes(appNavItemIdPrefix)
            ? item.htmlId.replace(appNavItemIdPrefix, '')
            : item.htmlId.replace(moduleNavItemIdPrefix, '');
          return itemId === module.id && item.canAccess;
        });

        if (moduleNavItem) {
          const navItemIconName =
            module.icon ||
            getModuleIcon(module.type, module.id, extensionsSettings);
          const navItemLabel =
            module.label ||
            getModuleName(module.type, module.id, extensionsSettings);

          const navItem: NavItem = {
            ...moduleNavItem,
            icon: IconNameToComponent[navItemIconName as IconType],
            iconName: navItemIconName,
            label: navItemLabel || '',
            id: module.id,
          };
          if (EnableAppSetup) {
            navItem.isHidden = !isClient && module.iuSidebarHidden;
          }
          if (module.path) {
            navItem.folderPath = module.path;
          }

          return navItem;
        }
        return null;
      })
      .filter(Boolean);

    if (!isClient && !EnableAppSetup) {
      // For internal admin users, core modules would always remain in same sort order.
      clientNavItems.sort((a, b) => {
        const aPriority = a.priority || 0;
        const bPriority = b.priority || 0;
        return aPriority - bPriority;
      });

      const internalNavItemsWithApps = clientNavItems.concat(extensionNavItems);

      // If the total number of apps is less than or equal to 10, return all apps
      if (internalNavItemsWithApps.length <= 10) {
        return { navItems: internalNavItemsWithApps };
      }

      // If the total number of apps is more than 10, split the array into two parts
      const splitIndex = internalNavItemsWithApps.length === 11 ? 9 : 10;
      const visibleItems = internalNavItemsWithApps.slice(0, splitIndex);
      const moreAppsNavItems = internalNavItemsWithApps.slice(splitIndex);

      // Create a new NavItem for the remaining apps
      const moreNavItem: NavItem = {
        ...extensionNavItem,
        subItems: moreAppsNavItems,
        path: moreAppsNavItems.at(0)?.path || '',
        shouldCollapse: moreAppsNavItems.some(
          (x) => x.path === `${pathname}?id=${queryIdParam}`,
        ),
      };

      // Return the visible items and the new NavItem
      return { navItems: [...visibleItems, moreNavItem] };
    }

    if (
      isClient &&
      routeAccess.allowPaymentsPage &&
      routeAccess.allowClientProductStore
    ) {
      const storeNavItem = defaultPortalSectionNavItems.find(
        (item) => item.htmlId === 'sidebar-client-store',
      );

      const billingIndexInClient = clientNavItems.findIndex(
        (item) => item.htmlId === 'sidebar-payments',
      );

      if (storeNavItem && billingIndexInClient !== -1) {
        const billingNavItem = clientNavItems[billingIndexInClient];
        // Inherit folderPath from the billing nav item so the store
        // gets nested in the same folder as billing for clients
        const storeNavItemWithFolder = billingNavItem?.folderPath
          ? { ...storeNavItem, folderPath: billingNavItem.folderPath }
          : storeNavItem;
        clientNavItems.splice(billingIndexInClient, 0, storeNavItemWithFolder);
      }
    }

    if (EnableAppSetup) {
      // move the contents of folders into their folder's subItems
      const navItemsWithFolders = nestItemsInFolders(clientNavItems);

      // Set shouldCollapse for folders based on current path
      navItemsWithFolders.forEach((item) => {
        if (item.isFolder && item.subItems) {
          item.shouldCollapse = item.subItems.some(
            (x) => x.selected || x.path === `${pathname}?id=${queryIdParam}`,
          );
        }
      });

      // folders should be hidden if all of their children are hidden
      navItemsWithFolders.forEach((item) => {
        if (
          item.isFolder &&
          !item.isHidden &&
          item.subItems &&
          !item.subItems.some((subItem) => !subItem.isHidden)
        ) {
          item.isHidden = true;
        }
      });

      // remove all folders in the navItems array
      const navItems = clientNavItems.reduce<NavItem[]>((acc, currItem) => {
        if (currItem.isFolder) {
          return acc;
        }
        return [...acc, currItem];
      }, []);

      return {
        navItems,
        navItemsWithFolders,
      };
    }

    return { navItems: clientNavItems };
  };

  const homeNavItems = React.useMemo(
    () => [
      homeNavItem,
      crmNavItem,
      inboxNavItem,
      ...(hasAutomationsAccess ? [automationNavItem] : []),
    ],
    [routeAccess],
  );

  const coreModulesNavItems: CoreModulesNavItems = React.useMemo(
    () => getCoreModulesFromModuleSettings(),
    [
      moduleSettings,
      extensionNavItem,
      isClient,
      extensionNavItems,
      extensionsSettings,
      planStatus,
    ],
  );

  const portalNavItems = React.useMemo(
    () => getPortalNavItems(),
    [routeAccess, isClient],
  );

  const mainSidebarSections: Array<NavSection> = React.useMemo(
    () => [
      {
        htmlId: 'section-core',
        title: '',
        navItems: homeNavItems,
        canAccess: homeNavItems.some((item) => item.canAccess),
      },
      {
        htmlId: 'section-modules',
        title: 'Apps',
        navItems: coreModulesNavItems.navItems,
        canAccess: coreModulesNavItems.navItems.some((item) => item.canAccess),
        navItemsWithFolders: coreModulesNavItems.navItemsWithFolders,
      },
      {
        htmlId: 'section-portal',
        title: 'Preferences',
        navItems: portalNavItems,
        canAccess: portalNavItems.some((x) => x.canAccess),
      },
    ],
    [homeNavItems, coreModulesNavItems, portalNavItems],
  );

  const workspaceSectionNavItems: Array<NavItem> = [
    {
      htmlId: 'main-setting-page',
      label: Constants.GENERAL_SETTINGS_PAGE.label,
      path: Constants.GENERAL_SETTINGS_PAGE.path,
      icon: Constants.GENERAL_SETTINGS_PAGE.icon,
      canAccess: routeAccess.allowSettingsMainPage,
      selected: pathname === SETTINGS_PAGE_ROOT_PATH,
      iconName: 'Settings',
    },
    {
      htmlId: 'assistant-settings-page',
      label: ASSISTANT_SETTINGS_PAGE.label,
      path: ASSISTANT_SETTINGS_PAGE.path,
      icon: ASSISTANT_SETTINGS_PAGE.icon,
      iconName: 'AssemblyLogo',
      canAccess: routeAccess.allowSettingsAssistant,
      selected: pathname.includes(ASSISTANT_SETTINGS_PAGE.path),
    },
    {
      htmlId: 'members-setting-page',
      label: Constants.SETTINGS_TEAM_PAGE.label,
      path: Constants.SETTINGS_TEAM_PAGE.path,
      icon: Constants.SETTINGS_TEAM_PAGE.icon,
      iconName: 'Teams',
      canAccess: routeAccess.allowSettingsMembersPage,
      children: (
        <UpgradeBadge
          show={
            hasModifyInternalRolePermission && planStatus.requireAdvancedUpgrade
          }
        />
      ),
    },

    {
      htmlId: 'payments-setting-page',
      label: PAYMENTS_PAGE.label,
      path: PAYMENTS_PAGE.path,
      icon: PAYMENTS_PAGE.icon,
      iconName: 'credit-card',
      canAccess: routeAccess.allowSettingsModulePayments,
    },
    {
      htmlId: 'api-settings-page',
      label: API_SETTINGS_PAGE.label,
      path: API_SETTINGS_PAGE.path,
      icon: API_SETTINGS_PAGE.icon,
      iconName: 'microchip',
      canAccess: routeAccess.allowSettingsApiPage,
      children: (
        <UpgradeBadge
          show={planStatus.isStarterPlan || planStatus.isTrialing}
        />
      ),
      selected: pathname.includes(API_SETTINGS_PAGE.path),
    },
    {
      htmlId: 'billing-setting-page',
      label: BILLING_PAGE.label,
      path: BILLING_PAGE.path,
      icon: BILLING_PAGE.icon,
      iconName: 'credit-card',
      canAccess: routeAccess.allowSettingsBillingPage,
    },

    {
      htmlId: 'plan-setting-page',
      label: PLAN_PAGE.label,
      path: PLAN_PAGE.path,
      icon: PLAN_PAGE.icon,
      iconName: 'PlansPayments',
      canAccess: routeAccess.allowSettingsPlanPage,
      selected: pathname.includes(PLAN_PAGE.path),
    },
  ];

  const clientExperienceItems: Array<NavItem> = [
    {
      htmlId: 'auth-settings-page',
      label: AUTH_SETTINGS_PAGE.label,
      path: AUTH_SETTINGS_PAGE.path,
      icon: AUTH_SETTINGS_PAGE.icon,
      canAccess: routeAccess.allowSettingsClientAuth,
      selected: pathname.includes(AUTH_SETTINGS_PAGE.path),
      iconName: 'lock',
    },
    {
      htmlId: 'client-notifications-page',
      label: CLIENT_NOTIFICATIONS_PAGE.label,
      path: CLIENT_NOTIFICATIONS_PAGE.path,
      icon: CLIENT_NOTIFICATIONS_PAGE.icon,
      iconName: 'bell',
      canAccess: routeAccess.allowSettingsClientNotificationsPage,
      selected: pathname.includes(CLIENT_NOTIFICATIONS_PAGE.path),
    },
    {
      htmlId: 'domain-setting-page',
      label: DOMAIN_PAGE.label,
      path: DOMAIN_PAGE.path,
      icon: DOMAIN_PAGE.icon,
      iconName: 'globe',
      canAccess: routeAccess.allowSettingsDomainPage,
      children: (
        <UpgradeBadge
          show={planStatus.isStarterPlan || planStatus.requireProUpgrade}
        />
      ),
    },
  ];

  const inAccessibleNavItemsForClient: Array<NavItem['label']> = [
    Constants.GENERAL_SETTINGS_PAGE.label,
    Constants.SETTINGS_TEAM_PAGE.label,
  ];

  const clientWorkspaceSectionNavItems = shouldFilterNonClientItems
    ? workspaceSectionNavItems.filter(
        (x) => !inAccessibleNavItemsForClient.includes(x.label),
      )
    : workspaceSectionNavItems;

  const settingsSidebarSections: Array<NavSection> = [
    {
      htmlId: 'section-about-settings',
      title: 'Me',
      navItems: meSectionNavItems,
      canAccess: meSectionNavItems.some((item) => item.canAccess),
    },
    {
      htmlId: 'section-workspace-settings',
      title: 'Workspace',
      navItems: clientWorkspaceSectionNavItems,
      canAccess: clientWorkspaceSectionNavItems.some((item) => item.canAccess),
    },
    {
      htmlId: 'section-client-experience',
      title: t(($) => $.navigation.client_experience),
      navItems: clientExperienceItems,
      canAccess: clientExperienceItems.some((item) => item.canAccess),
    },
  ];

  return {
    sections:
      sidebarType === 'settings'
        ? settingsSidebarSections
        : mainSidebarSections,
    isLoadingInstalls,
  };
};
